import { useMemo } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, MessageCircle, MapPin, Star } from "lucide-react";
import { LANDING_PAGES, CONSULTANTS } from "../data/landingPages";
import { CLINIC, waLink, telLink } from "../lib/clinic";
import { REF, SITE, breadcrumb, graph, PROVIDER_REF } from "../lib/schema";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import RelatedBlogPosts from "../components/site/RelatedBlogPosts";
import ServiceExtendedContent from "../components/site/ServiceExtendedContent";
import NewspaperFeature from "../components/site/NewspaperFeature";
import { getPressMentionsForTopic } from "../data/press";

const CityService = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const page = useMemo(() => LANDING_PAGES.find((p) => p.slug === slug), [slug]);
  const consultant = page ? CONSULTANTS[page.consultantSlug] : null;

  usePageMeta({
    title: page?.metaTitle,
    path: page ? `/${page.slug}` : undefined,
    description: page?.metaDescription,
  });

  // Build a rich page-scoped JSON-LD graph: WebPage + MedicalProcedure + Physician + FAQPage + Breadcrumb.
  const jsonLd = useMemo(() => {
    if (!page) return null;
    const pageUrl = `${SITE}/${page.slug}`;
    const nodes = [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Services in Varanasi", path: "/expertise" },
        { name: page.h1, path: `/${page.slug}` },
      ]),
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: page.metaTitle,
        description: page.metaDescription,
        isPartOf: { "@id": REF.website },
        about: { "@id": `${pageUrl}#procedure` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: consultant?.portrait || CLINIC.doctorPortrait,
        },
        mainEntity: { "@id": `${pageUrl}#procedure` },
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
        inLanguage: "en-IN",
      },
      {
        "@type": [page.procedureCategory || "MedicalProcedure", "MedicalProcedure"],
        "@id": `${pageUrl}#procedure`,
        name: page.procedureName,
        alternateName: page.keyword,
        description: page.intro,
        procedureType: "http://schema.org/NoninvasiveProcedure",
        howPerformed: page.sections?.[0]?.body,
        preparation: "Bring existing eye reports, glasses prescriptions and any relevant medical history.",
        followup: "Post-procedure follow-up as advised at your consultation.",
        bodyLocation: "Eye",
        performer: { "@id": consultant?.schemaId || REF.physician },
        location: { "@id": REF.clinic },
        provider: PROVIDER_REF,
        areaServed: [
          { "@type": "City", name: "Varanasi" },
          { "@type": "State", name: "Uttar Pradesh" },
        ],
        ...(page.canonicalExpertise
          ? { mainEntityOfPage: `${SITE}/expertise/${page.canonicalExpertise}` }
          : {}),
      },
      // If a non-Shantanu consultant leads this service, emit a lightweight Physician entity.
      page.consultantSlug === "manisha"
        ? {
            "@type": ["Physician", "Person"],
            "@id": consultant.schemaId,
            name: consultant.name,
            givenName: "Manisha",
            familyName: "Mishra",
            honorificPrefix: "Dr.",
            jobTitle: consultant.role,
            medicalSpecialty: ["Ophthalmic", "PediatricOphthalmology", "Strabismus"],
            worksFor: { "@id": REF.clinic },
            affiliation: { "@id": REF.clinic },
            areaServed: { "@type": "City", name: "Varanasi" },
          }
        : null,
      page.faqs?.length && {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    return graph(nodes);
  }, [page, consultant]);

  usePageJsonLd(jsonLd);

  if (!page) return <Navigate to="/expertise" replace />;

  const otherPages = LANDING_PAGES.filter((p) => p.slug !== page.slug).slice(0, 6);

  return (
    <div data-testid={`city-service-${page.slug}`}>
      {/* HERO */}
      <section className="pt-20 pb-8 container-page">
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            <li><Link to="/" className="hover:text-[hsl(var(--primary))]">Home</Link></li>
            <li>›</li>
            <li><Link to="/expertise" className="hover:text-[hsl(var(--primary))]">Services in Varanasi</Link></li>
            <li>›</li>
            <li className="text-[hsl(var(--foreground))]/80">{page.keyword}</li>
          </ol>
        </nav>
        <div className="overline mb-4" data-testid="landing-overline">{page.keyword}</div>
        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl"
          data-testid="landing-h1"
        >
          {page.h1}
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">{page.tagline}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={waLink(page.ctaMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="landing-cta-whatsapp"
          >
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
          <a href={telLink()} className="btn-ghost" data-testid="landing-cta-call">
            Call {CLINIC.phone}
          </a>
          <a
            href={CLINIC.googleProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))]/60 transition"
            data-testid="landing-google-verified"
          >
            <Star size={13} fill="currentColor" strokeWidth={0} className="text-[hsl(var(--accent))]" />
            {CLINIC.googleRating}★ on Google
          </a>
        </div>
      </section>

      {/* KEY POINTS */}
      <section className="py-8 container-page">
        <div className="grid sm:grid-cols-2 gap-4">
          {page.heroBullets.map((b, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-4 rounded-xl border border-border/70 bg-card/70 dark:bg-white/[0.03]"
              data-testid={`landing-bullet-${i}`}
            >
              <CheckCircle2 size={18} strokeWidth={1.75} className="mt-0.5 text-[hsl(var(--primary))] shrink-0" />
              <p className="text-sm leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CONSULTANT */}
      {consultant && (
        <section className="py-8 container-page" data-testid="landing-consultant">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">Consultant</div>
          <Link
            to={consultant.profileUrl || "/about"}
            data-testid={`landing-consultant-link-${page.consultantSlug}`}
            className="flex items-start gap-5 p-6 rounded-2xl border border-border bg-card/70 dark:bg-white/[0.03] hover:border-[hsl(var(--primary))]/50 hover:shadow-sm transition group"
          >
            <div
              className="w-20 h-20 rounded-full bg-[hsl(var(--primary))]/10 shrink-0 overflow-hidden ring-1 ring-border"
              aria-hidden="true"
            >
              {consultant.portrait ? (
                <SafeImage
                  src={consultant.portrait}
                  alt={consultant.name}
                  label={consultant.name}
                  loading="eager"
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="font-serif text-2xl text-[hsl(var(--primary))]">
                    {consultant.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="font-serif text-2xl leading-tight group-hover:text-[hsl(var(--primary))]">
                {consultant.name}
              </div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--accent))]">
                {consultant.role}
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--foreground))]/75">{consultant.credentials}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.16em] text-[hsl(var(--primary))] opacity-70 group-hover:opacity-100 transition">
                Read full profile <ArrowRight size={12} />
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* INTRO + SECTIONS */}
      <section className="py-8 container-page max-w-3xl">
        <p className="prose-elegant text-lg">{page.intro}</p>
        {page.sections?.map((s, i) => (
          <div key={i} className="mt-10">
            <h2 className="font-serif text-3xl">{s.heading}</h2>
            <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-4" />
            <p className="prose-elegant">{s.body}</p>
          </div>
        ))}
      </section>

      {/* FAQS */}
      {page.faqs?.length > 0 && (
        <section className="py-8 container-page max-w-3xl" data-testid="landing-faqs">
          <SectionHeader overline="Frequently Asked" title={`${page.keyword} — patient FAQs`} />
          <div className="space-y-4 mt-6">
            {page.faqs.map((f, i) => (
              <details key={i} className="group border border-border rounded-xl p-5 open:bg-card/70 dark:open:bg-white/[0.03]">
                <summary className="cursor-pointer list-none flex justify-between items-center font-medium">
                  <span>{f.q}</span>
                  <span className="text-[hsl(var(--accent))] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[hsl(var(--foreground))]/80">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* LOCATION */}
      <section className="py-8 container-page" data-testid="landing-location">
        <div className="rounded-2xl border border-border p-6 bg-[hsl(var(--muted))]/30">
          <div className="flex items-start gap-4">
            <MapPin size={20} strokeWidth={1.75} className="mt-1 text-[hsl(var(--primary))] shrink-0" />
            <div>
              <div className="font-serif text-xl">{CLINIC.name}</div>
              <p className="mt-1 text-sm text-muted-foreground">{CLINIC.address}</p>
              <p className="mt-1 text-xs text-muted-foreground">Plus Code: {CLINIC.plusCode}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <a
                  href={CLINIC.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--primary))] hover:underline"
                >
                  Get directions →
                </a>
                <span className="text-xs text-muted-foreground">{CLINIC.timings[0].hours} (Mon–Sat)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS MENTIONS — compact "In the news" strip for services whose
          topicHref matches a real press mention (e.g. the squint page).
          Rendered right below the hero so it's the first authority signal
          patients see on service pages. */}
      {getPressMentionsForTopic(`/${page.slug}`).slice(0, 1).map((m) => (
        <NewspaperFeature key={m.id} mention={m} variant="compact" />
      ))}

      {/* EXTENDED SEO CONTENT — 6 sections (steps, comparison table,
          recovery, misconceptions, when-not-to, insurance).  Data-driven
          from /data/serviceExtensions.js — silently no-ops if no data
          exists for this slug. */}
      <ServiceExtendedContent slug={page.slug} />

      {/* RELATED BLOG READING — SEO Audit Item C (internal linking pass).
          Display-only: reads from public /api/blog, no writes. */}
      <RelatedBlogPosts
        expertiseSlug={page.canonicalExpertise}
        keywords={[page.keyword, page.procedureName, ...(page.h1?.toLowerCase().split(/\s+/) || [])]}
        title={`Related reading on ${page.keyword}`}
        overline="Related reading"
        limit={3}
      />

      {/* CROSS-LINKS */}
      <section className="py-10 container-page" data-testid="landing-siblings">
        <SectionHeader overline="Other services in Varanasi" title="More at Shantanu Netralaya" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {otherPages.map((o) => (
            <Link
              key={o.slug}
              to={`/${o.slug}`}
              data-testid={`landing-sibling-${o.slug}`}
              className="group border border-border rounded-xl p-5 hover:border-[hsl(var(--primary))] transition-colors"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-1.5">
                {o.keyword}
              </div>
              <div className="font-serif text-lg leading-tight group-hover:text-[hsl(var(--primary))]">
                {o.h1}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition">
                Read more <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
        {page.canonicalExpertise && (
          <div className="mt-8">
            <Link
              to={`/expertise/${page.canonicalExpertise}`}
              className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] hover:underline"
              data-testid="landing-canonical-expertise-link"
            >
              ← Read the clinical detail page for this service
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default CityService;
